import { useState, useEffect, useRef, useCallback } from "react";
import { FaHandPaper, FaPaperPlane, FaWallet, FaCheck, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { useRadixConnect } from "../../../radix_connect";
import { RADIX_API_GATEWAY_URL, DCKSLAP_RESOURCE_ADDRESS } from "../../../constants";
import reddickImage from "../../../assets/reddickrednobackground.png";
import "./SendSlapPage.css";

// Sound imports
import roamingSound from "./sounds/Sound effect Ahhh.mp3";
import slapSound from "./sounds/cartoon-slap-2-189831.mp3";

// ═══════════════════════════════════════════════════════════════════════════════
// SEND SLAP PAGE - Premium integrated page component
// All-in-one page for selecting targets and sending DCKSLAPS
// ═══════════════════════════════════════════════════════════════════════════════

const SendSlapPage = () => {
  const { rdt, connected, selectedAccount } = useRadixConnect();
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [manualAddress, setManualAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showSendAnimation, setShowSendAnimation] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [targetForAnimation, setTargetForAnimation] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [slapsOwned, setSlapsOwned] = useState(0);
  const [showNoSlapsModal, setShowNoSlapsModal] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // FETCH DCKSLAP BALANCE
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (connected && selectedAccount) {
      const fetchBalance = async () => {
        try {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/entity/details",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                addresses: [selectedAccount.address],
                aggregation_level: "Vault",
              }),
            }
          );
          if (!response.ok) throw new Error("Network response was not ok");
          const data = await response.json();
          const entity = data.items[0];
          const fungible = entity?.fungible_resources?.items.find(
            (item) => item.resource_address === DCKSLAP_RESOURCE_ADDRESS
          );
          const totalAmount = fungible?.vaults?.items?.reduce((sum, vault) => {
            return sum + parseFloat(vault.amount || "0");
          }, 0) ?? 0;
          setSlapsOwned(Math.floor(totalAmount));
        } catch (error) {
          console.error("Failed to fetch DCKSLAP balance:", error);
          setSlapsOwned(0);
        }
      };
      fetchBalance();
    }
  }, [connected, selectedAccount]);

  // Audio refs for sound effects
  const roamingAudioRef = useRef(null);
  const slapAudioRef = useRef(null);

  // Play sound effect helper
  const playSound = useCallback((audioRef, soundSrc, volume = 1.0) => {
    try {
      if (!soundSrc) return;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      audioRef.current = new Audio(soundSrc);
      audioRef.current.volume = volume;
      audioRef.current.play().catch(() => {
        // Silently handle autoplay restrictions
      });
    } catch (err) {
      // Silently handle audio errors
    }
  }, []);

  // Stop all sounds
  const stopAllSounds = useCallback(() => {
    if (roamingAudioRef.current) {
      roamingAudioRef.current.pause();
      roamingAudioRef.current = null;
    }
    if (slapAudioRef.current) {
      slapAudioRef.current.pause();
      slapAudioRef.current = null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CHARACTER DATA
  // ─────────────────────────────────────────────────────────────────────────────

  const characters = [
    {
      id: "piers",
      name: "Piers",
      image: require("./images/piersslap.png"),
      address: "account_rdx12y7up9wwuvrlp4upvlgt9ka222p6u9dnqfa0ldygcn7qatl9e6k0za",
    },
    {
      id: "trump",
      name: "President Trump",
      image: require("./images/trump.png"),
      address: "account_rdx16y28dextdn0sleutgylq4dde08vd83zhhkwaxp0ygzldpdms5u08an",
    },
    {
      id: "adam",
      name: "Adam",
      image: require("./images/adam.png"),
      address: "account_rdx168w3g79ea7yh6ge07y4zkd8vgmxsyaelhvyk4ngye5p5use69mzag2",
    },
    {
      id: "rick",
      name: "Rick",
      image: require("./images/rick.png"),
      address: "account_rdx12xa75arlle03cquxrh37wnvpavfyhu7qxnu96q2jj8p0ytdjy24wkr",
    },
    {
      id: "sasha",
      name: "Sasha Slapworthy",
      image: require("./images/Sasha.png"),
      address: "account_rdx128yzt39m9a47lx3r8zc2xdyc8rquh56qyrke7a0lsxg9u6dppzc2rv",
      imageStyle: { objectFit: "contain", transform: "scale(1.25)" },
    },
    {
      id: "loganpaul",
      name: "Logan Paul",
      image: require("./images/loganpaul.png"),
      address: "account_rdx168y70j69fz78xjy7tdwcka9vfsprnpu6leep92vv7cmmwv3s4vyh6g",
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
    setManualAddress(""); // Clear manual address when character is selected
    setAddressError(""); // Clear any errors
  };

  const handleManualAddressChange = (e) => {
    const address = e.target.value;
    setManualAddress(address);
    setSelectedCharacter(null); // Clear character selection when manual address is entered
    setAddressError(""); // Clear any errors

    // Validate address format
    if (address && !address.startsWith("account_")) {
      setAddressError('Address must start with "account_"');
    }
  };

  const canSend = () => {
    return (
      selectedCharacter ||
      (manualAddress && manualAddress.startsWith("account_") && !addressError)
    );
  };

  const handleSendSlap = async () => {
    if (!canSend() || isSending) return;

    // Guard: check if user has at least 1 DCKSLAP
    if (slapsOwned < 1) {
      setShowNoSlapsModal(true);
      return;
    }

    setIsSending(true);
    setShowSendAnimation(true);

    const target = selectedCharacter || {
      id: "manual",
      address: manualAddress,
      name: "Manual Address",
    };

    const recipientAddress = selectedCharacter?.address || manualAddress;
    if (!recipientAddress) {
      console.error("No recipient address found");
      setIsSending(false);
      setShowSendAnimation(false);
      return;
    }

    const manifest = `
        CALL_METHOD
          Address("${selectedAccount.address}")
          "withdraw"
          Address("${DCKSLAP_RESOURCE_ADDRESS}")
          Decimal("1.0")
          ;
        TAKE_FROM_WORKTOP
          Address("${DCKSLAP_RESOURCE_ADDRESS}")
          Decimal("1.0")
          Bucket("dcksl_bucket")
          ;
        CALL_METHOD
          Address("${recipientAddress}")
          "try_deposit_or_abort"
          Bucket("dcksl_bucket")
          None
          ;
        `;

    const result = await rdt.walletApi.sendTransaction({
      transactionManifest: manifest,
      message: `You've just been DCKSLAPPED! Join the Slapfest https://www.reddicks.meme/dckslap.`,
    });

    if (result.isErr()) {
      setIsSending(false);
      setShowSendAnimation(false);
    } else {
      setShowSendAnimation(false);
      setSlapsOwned((prev) => prev - 1);

      // Start the epic success animation!
      setTargetForAnimation(target);
      setShowSuccessAnimation(true);
      setAnimationPhase(1);

      // Play the roaming "Ahhh" sound at the start (4 seconds long)
      playSound(roamingAudioRef, roamingSound, 0.7);

      // Phase progression for the animation
      setTimeout(() => setAnimationPhase(2), 600);
      setTimeout(() => {
        setAnimationPhase(3);
        playSound(slapAudioRef, slapSound, 0.9);
      }, 4000);
      setTimeout(() => setAnimationPhase(4), 4500);
      setTimeout(() => setAnimationPhase(5), 5000);
      setTimeout(() => setAnimationPhase(6), 5500);

      // End animation
      setTimeout(() => {
        stopAllSounds();
        setShowSuccessAnimation(false);
        setTargetForAnimation(null);
        setAnimationPhase(0);
        setIsSending(false);
        setSelectedCharacter(null);
        setManualAddress("");
        setAddressError("");
      }, 6500);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ANIMATION HELPERS
  // ─────────────────────────────────────────────────────────────────────────────

  const generateParticles = (count) => {
    const particles = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * 360;
      const delay = Math.random() * 0.2;
      particles.push(
        <div
          key={i}
          className="slap-particle"
          style={{
            "--angle": `${angle}deg`,
            "--delay": `${delay}s`,
            "--distance": `${80 + Math.random() * 120}px`,
          }}
        />
      );
    }
    return particles;
  };

  const generateStars = (count) => {
    const stars = [];
    for (let i = 0; i < count; i++) {
      const left = Math.random() * 100;
      const delay = Math.random() * 0.5;
      const size = 10 + Math.random() * 20;
      stars.push(
        <div
          key={i}
          className="victory-star"
          style={{
            "--left": `${left}%`,
            "--delay": `${delay}s`,
            "--size": `${size}px`,
          }}
        />
      );
    }
    return stars;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
      {/* Epic Success Animation Overlay */}
      {showSuccessAnimation && targetForAnimation && (
        <div className={`slap-success-overlay phase-${animationPhase}`}>
          <div className="mega-flash" />

          <div className="target-face-container">
            <img
              src={
                targetForAnimation.image?.default ||
                targetForAnimation.image ||
                require("./images/piersslap.png")
              }
              alt="Target"
              className="target-face"
            />

            <div className="impact-shockwave impact-1" />
            <div className="impact-shockwave impact-2" />
            <div className="impact-shockwave impact-3" />

            <div className="particles-container">
              {animationPhase >= 3 && generateParticles(24)}
            </div>
          </div>

          <div className="reddick-slapper left">
            <img src={reddickImage} alt="Reddick" />
            <div className="motion-trail" />
          </div>

          <div className="reddick-slapper right">
            <img src={reddickImage} alt="Reddick" />
            <div className="motion-trail" />
          </div>

          <div className="slapped-text">
            <span>S</span>
            <span>L</span>
            <span>A</span>
            <span>P</span>
            <span>P</span>
            <span>E</span>
            <span>D</span>
            <span>!</span>
          </div>

          <div className="victory-stars">
            {animationPhase >= 5 && generateStars(15)}
          </div>

          {animationPhase >= 3 && animationPhase < 5 && (
            <div className="screen-shake-overlay" />
          )}
        </div>
      )}

      {/* Page Header */}
      <div className="text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-bold text-primary">
          Send a DCKSLAP
        </h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Choose your target and send them a DCKSLAP they won't forget!
        </p>
      </div>

      {/* Main Content Card */}
      <Card
        className={`relative overflow-hidden ${showSendAnimation ? "send-charging" : ""}`}
        style={{
          background: "linear-gradient(180deg, hsl(0 5% 9%) 0%, hsl(0 8% 6%) 100%)",
        }}
      >
        {/* Send Pending Overlay */}
        {showSendAnimation && (
          <div className="send-pending-overlay">
            <div className="send-pending-glow" />
            <div className="send-pending-content">
              <FaHandPaper className="send-pending-icon" />
              <p className="send-pending-text">Awaiting Wallet<span className="send-pending-dots">...</span></p>
              <p className="send-pending-subtext">Approve the transaction in your Radix Wallet</p>
            </div>
          </div>
        )}

        <CardHeader className="text-center pb-4">
          <CardTitle className="text-xl md:text-2xl" style={{ color: "hsl(0 30% 65%)" }}>
            Select Your Target
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Pick who deserves your DCKSLAP the most:
          </p>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Characters Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
            {characters.map((character) => (
              <button
                key={character.id}
                onClick={() => !isSending && handleCharacterSelect(character)}
                disabled={isSending}
                className={`
                  group relative flex flex-col items-center p-4 md:p-5
                  rounded-xl border-2 transition-all duration-300
                  ${selectedCharacter?.id === character.id
                    ? "border-primary bg-primary/10 shadow-glow"
                    : "border-border hover:border-primary/50 bg-card"
                  }
                  ${isSending ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:-translate-y-1"}
                `}
                aria-label={`Select ${character.name}`}
                aria-selected={selectedCharacter?.id === character.id}
              >
                {/* Selection Badge */}
                <div
                  className={`
                    absolute top-2 right-2 w-7 h-7 rounded-full
                    flex items-center justify-center
                    transition-all duration-200
                    ${selectedCharacter?.id === character.id
                      ? "bg-primary scale-100 opacity-100"
                      : "bg-muted scale-75 opacity-0 group-hover:opacity-30"
                    }
                  `}
                >
                  {selectedCharacter?.id === character.id ? (
                    <FaCheck className="text-white text-xs" />
                  ) : (
                    <FaHandPaper className="text-muted-foreground text-xs" />
                  )}
                </div>

                {/* Avatar */}
                <div
                  className={`
                    w-20 h-20 md:w-28 md:h-28 rounded-full overflow-hidden
                    border-2 transition-all duration-300
                    ${selectedCharacter?.id === character.id
                      ? "border-primary shadow-glow"
                      : "border-border group-hover:border-primary/50"
                    }
                  `}
                  style={{
                    background: "linear-gradient(180deg, hsl(0 10% 15%) 0%, hsl(0 15% 10%) 100%)",
                  }}
                >
                  <img
                    src={character.image.default || character.image}
                    alt={`${character.name} avatar`}
                    className="w-full h-full object-cover"
                    style={character.imageStyle || {}}
                  />
                </div>

                {/* Name */}
                <h3
                  className={`
                    mt-3 text-sm md:text-base font-semibold text-center
                    transition-colors duration-200 line-clamp-2
                    ${selectedCharacter?.id === character.id
                      ? "text-primary"
                      : "text-foreground group-hover:text-primary"
                    }
                  `}
                >
                  {character.name}
                </h3>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground px-2">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Manual Address Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-foreground">
              <FaWallet className="text-muted-foreground" />
              <h3 className="text-lg font-semibold">Custom Address</h3>
            </div>

            <div className="max-w-md mx-auto space-y-2">
              <input
                type="text"
                value={manualAddress}
                onChange={handleManualAddressChange}
                placeholder="account_rdx..."
                disabled={isSending}
                className={`
                  w-full px-4 py-3 rounded-xl
                  bg-secondary/50 border-2 transition-all duration-200
                  text-sm font-mono text-foreground
                  placeholder:text-muted-foreground
                  focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${addressError
                    ? "border-destructive focus:border-destructive"
                    : manualAddress
                      ? "border-primary"
                      : "border-border focus:border-primary"
                  }
                `}
                aria-label="Enter recipient address"
              />
              {addressError && (
                <p className="text-sm text-destructive text-center" role="alert">
                  {addressError}
                </p>
              )}
            </div>
          </div>

          {/* Send Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleSendSlap}
              disabled={!canSend() || isSending}
              size="xl"
              className="min-w-[200px] gap-3 text-lg font-bold uppercase tracking-wider"
              aria-label={`Send slap to ${selectedCharacter?.name || manualAddress || "recipient"}`}
            >
              {isSending ? (
                <>
                  <span className="animate-pulse">Slapping...</span>
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Slap em'!
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Footer */}
      <div className="text-center text-sm text-muted-foreground">
        <p>
          Each DCKSLAP costs <span className="text-primary font-semibold">1 DCKSLAP token</span> from your wallet
        </p>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes flash {
          0% { opacity: 0; }
          50% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>

      {/* No Slaps Modal */}
      {showNoSlapsModal && (
        <div className="insufficient-modal-overlay">
          <div
            className="insufficient-modal-backdrop"
            onClick={() => setShowNoSlapsModal(false)}
          />
          <div className="insufficient-modal-content">
            <button
              className="insufficient-modal-close"
              onClick={() => setShowNoSlapsModal(false)}
            >
              <FaTimes />
            </button>
            <div className="insufficient-modal-icon">
              <FaExclamationTriangle />
            </div>
            <h3 className="insufficient-modal-title">
              No DCKSLAP Tokens
            </h3>
            <p className="insufficient-modal-message">
              You need at least <strong>1 DCKSLAP</strong> in your wallet to send a slap.
              <br />
              Head back to the Spank Bank to withdraw or buy more!
            </p>
            <div className="insufficient-modal-actions">
              <button
                className="insufficient-modal-btn primary"
                onClick={() => setShowNoSlapsModal(false)}
              >
                <span>Got It</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSlapPage;